import { useState, useEffect, useCallback, useRef } from 'react';
import { ProjectMemberService, ProjectRole, ProjectAccessResult } from '../lib/projectMemberService';
import { useAuth } from '../context/AuthContext';

export interface ProjectPermissions {
    role: ProjectRole;
    isLoading: boolean;
    // Permission flags
    canChat: boolean;           // All roles (owner, editor, viewer)
    canViewPages: boolean;      // All roles
    canEdit: boolean;           // Owner, Editor only
    canDelete: boolean;         // Owner only
    canManageMembers: boolean;  // Owner only
    canAddToPlaylist: boolean;  // Owner, Editor only
    canChangeTrack: boolean;    // All roles
    canLeave: boolean;          // Editor, Viewer only (not owner)
    // Computed
    isOwner: boolean;
    isEditor: boolean;
    isViewer: boolean;
    isMember: boolean;          // Any accepted member (not owner)
}

const DEFAULT_PERMISSIONS: ProjectPermissions = {
    role: null,
    isLoading: true,
    canChat: false,
    canViewPages: false,
    canEdit: false,
    canDelete: false,
    canManageMembers: false,
    canAddToPlaylist: false,
    canChangeTrack: false,
    canLeave: false,
    isOwner: false,
    isEditor: false,
    isViewer: false,
    isMember: false
};

/**
 * Hook to check and manage project permissions for the current user
 */
export function useProjectPermissions(projectId: string | null | undefined): ProjectPermissions {
    const { user } = useAuth();
    const userId = user?.id;
    const [permissions, setPermissions] = useState<ProjectPermissions>(DEFAULT_PERMISSIONS);
    const identity = `${userId || ''}:${projectId || ''}`;
    const [checkedIdentity, setCheckedIdentity] = useState('');
    const generation = useRef(0);

    const checkPermissions = useCallback(async () => {
        const request = ++generation.current;
        if (!projectId || !userId) {
            setCheckedIdentity(identity);
            setPermissions({ ...DEFAULT_PERMISSIONS, isLoading: false });
            return;
        }

        setPermissions(prev => ({ ...prev, isLoading: true }));

        try {
            const result: ProjectAccessResult = await ProjectMemberService.checkProjectAccess(projectId);
            if (request !== generation.current) return;
            setCheckedIdentity(identity);

            const role = result.role;
            const hasAccess = result.hasAccess;

            const isOwner = role === 'owner';
            const isEditor = role === 'editor';
            const isViewer = role === 'viewer';
            const isMember = isEditor || isViewer;

            setPermissions({
                role,
                isLoading: false,
                // All roles with access can chat and view
                canChat: hasAccess,
                canViewPages: hasAccess,
                // Only owner and editor can edit
                canEdit: isOwner || isEditor,
                // Only owner can delete
                canDelete: isOwner,
                // Only owner can manage members
                canManageMembers: isOwner,
                // Only owner and editor can add to playlist
                canAddToPlaylist: isOwner || isEditor,
                // All roles can change track
                canChangeTrack: hasAccess,
                // Only members (not owner) can leave
                canLeave: isMember,
                // Computed flags
                isOwner,
                isEditor,
                isViewer,
                isMember
            });
        } catch (error) {
            if (request !== generation.current) return;
            setCheckedIdentity(identity);
            console.error('useProjectPermissions: Error checking permissions', error);
            setPermissions({ ...DEFAULT_PERMISSIONS, isLoading: false });
        }
    }, [projectId, userId, identity]);

    useEffect(() => {
        checkPermissions();
        return () => { generation.current++; };
    }, [checkPermissions]);

    return checkedIdentity === identity ? permissions : { ...DEFAULT_PERMISSIONS, isLoading: !!(projectId && user) };
}

/**
 * Simplified hook that returns just the role without all permission flags
 */
export function useProjectRole(projectId: string | null): { role: ProjectRole; isLoading: boolean } {
    const { user } = useAuth();
    const [state, setState] = useState<{ role: ProjectRole; isLoading: boolean }>({
        role: null,
        isLoading: true
    });

    useEffect(() => {
        if (!projectId || !user) {
            setState({ role: null, isLoading: false });
            return;
        }

        setState(prev => ({ ...prev, isLoading: true }));

        ProjectMemberService.checkProjectAccess(projectId)
            .then(result => {
                setState({ role: result.role, isLoading: false });
            })
            .catch(() => {
                setState({ role: null, isLoading: false });
            });
    }, [projectId, user]);

    return state;
}

export default useProjectPermissions;
